import type { Client } from '@snapshots/transport';
import type { Capture, Facts, Files, FileText, Listing, Order, Showing, Storage } from './types';

/** What a listing asks for: the scope, the words, the state, the order and the page */
export interface Query {
	project?: string;
	organization?: string;
	text?: string;
	state?: Showing;
	sort?: Order;
	page?: number;
	limit?: number;
}

/**
 * The snapshots a person may see, and what they may still do to one: read what its packages hold, give it a
 * title, cancel it while it is pending, delete it recoverably for a period and restore it. Making a snapshot
 * is Packages'; nothing here uploads bytes.
 */
export class Captures {
	#client: Client;
	#facts?: Promise<Facts>;

	constructor(client: Client) {
		this.#client = client;
	}

	/**
	 * One page of the snapshots the person may see: of every organization of theirs, of one of them or of
	 * one project, searched, filtered and ordered as asked, with how many match in all
	 */
	async find(query: Query = {}): Promise<Listing> {
		const parameters = new URLSearchParams();
		for (const [key, value] of Object.entries({ project: query.project, organization: query.organization, q: query.text, state: query.state, sort: query.sort, page: query.page, limit: query.limit })) {
			if (value !== undefined && value !== '') parameters.set(key, String(value));
		}
		const search = parameters.toString();
		return this.#client.get<Listing>(`/v1/captures${search ? `?${search}` : ''}`);
	}

	/**
	 * What the service says of itself (`GET /v1/interface`), read once: the recovery period, the quota and
	 * how long snapshots usually take. Asked again later when it could not be read
	 */
	facts(): Promise<Facts> {
		this.#facts ??= this.#client.get<Facts>('/v1/interface').catch(() => {
			this.#facts = undefined;
			return {};
		});
		return this.#facts;
	}

	/** Days a deleted snapshot stays recoverable, as the service says; undefined when it does not */
	async recovery(): Promise<number | undefined> {
		const { recovery } = await this.facts();
		return typeof recovery === 'number' && recovery > 0 ? recovery : undefined;
	}

	async get(id: string): Promise<Capture> {
		return (await this.#client.get<{ capture: Capture }>(`/v1/captures/${encodeURIComponent(id)}`)).capture;
	}

	async retitle(id: string, title: string): Promise<Capture> {
		return (await this.#client.patch<{ capture: Capture }>(`/v1/captures/${encodeURIComponent(id)}`, { title })).capture;
	}

	async cancel(id: string): Promise<Capture> {
		return (await this.#client.post<{ capture: Capture }>(`/v1/captures/${encodeURIComponent(id)}/cancel`)).capture;
	}

	async delete(id: string): Promise<Capture> {
		return (await this.#client.delete<{ capture: Capture }>(`/v1/captures/${encodeURIComponent(id)}`)).capture;
	}

	async restore(id: string): Promise<Capture> {
		return (await this.#client.post<{ capture: Capture }>(`/v1/captures/${encodeURIComponent(id)}/restore`)).capture;
	}

	/** The files of one package of a completed snapshot */
	files(id: string, path: string): Promise<Files> {
		return this.#client.get<Files>(`/v1/captures/${encodeURIComponent(id)}/files?${new URLSearchParams({ package: path })}`);
	}

	/** One file of one package, with its text when it is text */
	async file(id: string, path: string, name: string): Promise<FileText> {
		return (await this.#client.get<{ file: FileText }>(`/v1/captures/${encodeURIComponent(id)}/file?${new URLSearchParams({ package: path, name })}`)).file;
	}

	/** What an organization's snapshots hold against its quota */
	async storage(organization: string): Promise<Storage> {
		return (await this.#client.get<{ storage: Storage }>(`/v1/organizations/${encodeURIComponent(organization)}/storage`)).storage;
	}

	/**
	 * Where a package's archive is downloaded: the query form, which serves every path, the root package `.`
	 * included. A link a person follows; never fetched by this application
	 */
	archive(capture: Capture, path: string): string {
		return `${this.#client.origin}/v1/captures/${encodeURIComponent(capture.id)}/archive?${new URLSearchParams({ package: path })}`;
	}
}
